// ========================================
// OPTIMIZED CART SCRIPTS - FAST VERSION
// ========================================

/**
 * Central Cart Manager with Gift Enforcement
 */
(() => {
	let cartCache = null;
	let fetchPromise = null;
	const subscribers = new Set();

	// Config
	const SHIP_THRESH = 10000; // $100
	const SNAP_THRESH = 20000; // $200
	const SNAPBACK_GIFT_ID = 42627343548505;
	const SNAPBACK_GIFT_KEY = "Free Gift";

	// Store original fetch before we override it
	const origFetch = window.fetch.bind(window);

	// Helpers
	const fmt = (c) => "$" + ((c || 0) / 100).toFixed(2);
	const fmtClean = (c) => "$" + Math.round((c || 0) / 100);

	const isSnapbackGift = (li) => {
		if (!li) return false;
		// Check variant ID
		if (li.variant_id === SNAPBACK_GIFT_ID) return true;
		// Check properties
		if (li.properties && li.properties[SNAPBACK_GIFT_KEY] === "Snapback")
			return true;
		return false;
	};

	const isFreeGiftTee = (li) =>
		(li?.title || "").toLowerCase().includes("free gift (tee)");

	const getSubtotal = (cart) => {
		if (!cart?.items?.length) return 0;
		let total = 0;
		for (const item of cart.items) {
			const title = (item.title || "").toLowerCase();
			// Skip free gifts and package protection
			if (item.price === 0) continue;
			if (item.product_handle === "package-protection") continue;
			if (title.includes("free gift")) continue;
			if (title.includes("free snapback")) continue;
			if (item.properties) {
				const freeGiftVal = item.properties["Free Gift"];
				if (freeGiftVal && /^(Snapback|Tee|GWP)$/i.test(String(freeGiftVal)))
					continue;
			}
			total += item.final_line_price || 0;
		}
		return total;
	};

	const hasToolbox = (cart) =>
		cart.items?.some((i) => (i.title || "").toLowerCase().includes("toolbox"));

	// Direct fetch helper (no interception)
	const directFetch = async (url, options) => {
		const res = await origFetch(url, options);
		return res;
	};

	// Fetch cart
	const fetchCart = async (force = false) => {
		if (!force && cartCache) return cartCache;
		if (fetchPromise) return fetchPromise;

		fetchPromise = directFetch("/cart.js")
			.then((r) => r.json())
			.then((cart) => {
				cartCache = cart;
				fetchPromise = null;
				return cart;
			})
			.catch((err) => {
				console.error("[CartManager] Fetch error:", err);
				fetchPromise = null;
				return cartCache || { items: [], total_price: 0, attributes: {} };
			});

		return fetchPromise;
	};

	// Notify subscribers
	const notify = (cart = cartCache) => {
		if (!cart) return;
		subscribers.forEach((cb) => {
			try {
				cb(cart);
			} catch (_) {}
		});
		document.dispatchEvent(
			new CustomEvent("theme:cart:change", { detail: { cart } }),
		);
	};

	// ========== FREE GIFT TEE ENFORCEMENT ==========
	let enforcingTee = false;
	const enforceSingleFreeGiftTee = async (cart) => {
		if (!cart?.items || enforcingTee) return;

		const tees = cart.items.filter(isFreeGiftTee);
		const needsFix = tees.length > 1 || tees.some((t) => t.quantity > 1);
		if (!needsFix) return;

		enforcingTee = true;
		try {
			const updates = {};
			tees.forEach((t, i) => {
				if (!t.key) return;
				updates[t.key] = i === 0 ? 1 : 0;
			});

			if (Object.keys(updates).length) {
				await directFetch("/cart/update.js", {
					method: "POST",
					headers: { "Content-Type": "application/json" },
					body: JSON.stringify({ updates }),
				});
				cartCache = await directFetch("/cart.js").then((r) => r.json());
				notify(cartCache);
				document.dispatchEvent(new CustomEvent("theme:cart:reload"));
			}
		} catch (e) {
			console.error("[FreeGiftTee] Error:", e);
		} finally {
			enforcingTee = false;
		}
	};

	// ========== SNAPBACK GWP ENFORCEMENT ==========
	let enforcingSnapback = false;
	const enforceSnapbackGWP = async (cart) => {
		if (!cart?.items || enforcingSnapback) return;

		const gift = cart.items.find(isSnapbackGift);
		const sub = getSubtotal(cart);
		const snapOk = sub >= SNAP_THRESH;
		const shouldHaveGift = snapOk;
		const hasGift = !!gift;
		const correctQty = !gift || gift.quantity === 1;

		// No action needed
		if (shouldHaveGift === hasGift && correctQty) return;

		enforcingSnapback = true;
		let changed = false;

		try {
			if (shouldHaveGift && !hasGift) {
				const res = await directFetch("/cart/add.js", {
					method: "POST",
					headers: { "Content-Type": "application/json" },
					body: JSON.stringify({
						items: [
							{
								id: SNAPBACK_GIFT_ID,
								quantity: 1,
								properties: { [SNAPBACK_GIFT_KEY]: "Snapback" },
							},
						],
					}),
				});
				changed = res.ok;
				if (!res.ok) console.error("[Snapback] Add failed:", await res.text());
			} else if (!shouldHaveGift && hasGift) {
				const res = await directFetch("/cart/change.js", {
					method: "POST",
					headers: { "Content-Type": "application/json" },
					body: JSON.stringify({ id: gift.key, quantity: 0 }),
				});
				changed = res.ok;
			} else if (hasGift && gift.quantity > 1) {
				const res = await directFetch("/cart/change.js", {
					method: "POST",
					headers: { "Content-Type": "application/json" },
					body: JSON.stringify({ id: gift.key, quantity: 1 }),
				});
				changed = res.ok;
			}

			// Refresh cart UI if we made changes
			if (changed) {
				cartCache = await directFetch("/cart.js").then((r) => r.json());
				notify(cartCache);
				updateShippingUI(cartCache);
				// Trigger theme cart reload to refresh upcart drawer
				document.dispatchEvent(new CustomEvent("theme:cart:reload"));
			}
		} catch (e) {
			console.error("[Snapback] Error:", e);
		} finally {
			enforcingSnapback = false;
		}
	};

	// ========== TEE COUNT HELPER ==========
	const countTees = (cart) => {
		if (!cart?.items) return 0;
		let count = 0;
		for (const item of cart.items) {
			if (item.price === 0) continue;
			if (item.properties?.["Free Gift"]) continue;
			const title = (item.title || "").toLowerCase();
			const type = (item.product_type || "").toLowerCase();
			if (title.includes("free gift")) continue;
			if (title.includes("bundle")) continue;
			if (type.includes("tee") || title.includes("tee")) {
				count += item.quantity;
			}
		}
		return count;
	};

	// ========== DEAL BANNER UI ==========
	const DEAL_THRESH = 2; // need 2 tees to unlock the 3rd for $10

	const updateDealUI = (cart) => {
		const dealSection = document.getElementById("dealSection");
		const dealCurrent = document.getElementById("dealCurrent");
		const dealMsg = document.getElementById("dealMsg");
		const banner = document.getElementById("promoBanner");

		if (!dealSection || !dealCurrent || !dealMsg) return;

		const tees = countTees(cart);
		const unlocked = tees >= DEAL_THRESH;

		if (tees >= 3) {
			// 3+ tees — deal active, hide banner
			dealSection.style.display = "none";
			if (banner) banner.classList.remove("deal-visible");
		} else {
			dealSection.style.display = "";
			if (banner) banner.classList.add("deal-visible");

			// Update count
			dealCurrent.textContent = Math.min(tees, DEAL_THRESH);

			// Update state
			dealSection.classList.toggle("unlocked", unlocked);

			// Update message
			if (unlocked) {
				dealMsg.textContent = "DEAL UNLOCKED — ADD YOUR 3RD TEE!";
			} else {
				const remaining = DEAL_THRESH - tees;
				dealMsg.textContent = "Add " + remaining + " tee" + (remaining > 1 ? "s" : "") + " to unlock";
			}
		}
	};

	// ========== SHIPPING PROGRESS UI ==========
	const updateShippingUI = (cart) => {
		if (!cart?.items) return;

		// Update deal progress
		updateDealUI(cart);

		const sub = getSubtotal(cart);
		const tb = hasToolbox(cart);
		const shipOk = sub >= SHIP_THRESH || tb;
		const snapOk = sub >= SNAP_THRESH;

		const promoBanner = document.getElementById("promoBanner");
		const shipHeader = document.getElementById("shipHeader");
		const shipSubtext = document.getElementById("shipSubtext");
		const shipFill = document.getElementById("shipFill");
		const shipGoal = document.getElementById("shipGoal");

		if (promoBanner && shipHeader && shipSubtext && shipFill && shipGoal) {
			if (snapOk) {
				promoBanner.classList.add("ship-unlocked");
				shipHeader.textContent = "FREE Shipping + FREE Snapback";
				shipSubtext.textContent = "You've maxed out your rewards!";
				shipGoal.textContent = "";
				shipFill.style.width = "100%";
			} else if (shipOk) {
				promoBanner.classList.remove("ship-unlocked");
				shipHeader.textContent = "FREE Shipping Unlocked!";
				shipSubtext.innerHTML =
					'— add <span class="amount">' +
					fmt(SNAP_THRESH - sub) +
					"</span> more for a <strong>FREE Snapback</strong>";
				shipGoal.textContent = fmtClean(SNAP_THRESH);
				const pct = ((sub - SHIP_THRESH) / (SNAP_THRESH - SHIP_THRESH)) * 100;
				shipFill.style.width = Math.min(100, Math.max(0, pct)) + "%";
			} else {
				promoBanner.classList.remove("ship-unlocked");
				shipHeader.textContent = "FREE Shipping";
				shipSubtext.innerHTML =
					'— add <span class="amount">' +
					fmt(SHIP_THRESH - sub) +
					"</span> more";
				shipGoal.textContent = fmtClean(SHIP_THRESH);
				const pct = (sub / SHIP_THRESH) * 100;
				shipFill.style.width = Math.min(100, Math.max(0, pct)) + "%";
			}
		}
	};

	// ========== MAIN CART CHANGE HANDLER ==========
	let handlingChange = false;
	let pendingChange = false;

	const handleCartChange = async () => {
		if (handlingChange) {
			pendingChange = true;
			return;
		}
		handlingChange = true;

		try {
			const cart = await fetchCart(true);

			// Update UI immediately
			updateShippingUI(cart);
			notify(cart);

			// Run enforcement (sequentially to avoid conflicts)
			await enforceSingleFreeGiftTee(cart);
			await enforceSnapbackGWP(cartCache); // Use updated cache after tee enforcement

			// Final notify with any enforcement changes
			notify(cartCache);
		} finally {
			handlingChange = false;
			// If another change came in while we were handling, process it
			if (pendingChange) {
				pendingChange = false;
				handleCartChange();
			}
		}
	};

	// ========== INTERCEPT FETCH ==========
	window.fetch = function (...args) {
		const url = typeof args[0] === "string" ? args[0] : args[0]?.url;
		const isCartMutation =
			url &&
			(url.includes("/cart/add") ||
				url.includes("/cart/change") ||
				url.includes("/cart/update") ||
				url.includes("/cart/clear"));

		return origFetch.apply(this, args).then((res) => {
			if (isCartMutation && res.ok) {
				// Run cart change handler after mutation
				handleCartChange();
			}
			return res;
		});
	};

	// ========== INTERCEPT XHR ==========
	const origOpen = XMLHttpRequest.prototype.open;
	XMLHttpRequest.prototype.open = function (_method, url, ...rest) {
		this._cartUrl = url;
		return origOpen.call(this, _method, url, ...rest);
	};
	const origSend = XMLHttpRequest.prototype.send;
	XMLHttpRequest.prototype.send = function (...args) {
		const url = this._cartUrl;
		if (
			url &&
			(url.includes("/cart/add") ||
				url.includes("/cart/change") ||
				url.includes("/cart/update") ||
				url.includes("/cart/clear"))
		) {
			this.addEventListener(
				"load",
				() => {
					if (this.status < 300) handleCartChange();
				},
				{ once: true },
			);
		}
		return origSend.apply(this, args);
	};

	// Events
	document.addEventListener("cart:updated", handleCartChange, {
		passive: true,
	});
	document.addEventListener("cart:refresh", handleCartChange, {
		passive: true,
	});

	// Expose CartManager
	window.CartManager = {
		subscribe: (cb) => {
			subscribers.add(cb);
			if (cartCache) cb(cartCache);
			return () => subscribers.delete(cb);
		},
		getCart: () => fetchCart(),
		refresh: handleCartChange,
	};

	// Init
	handleCartChange();
})();

/**
 * Cart Totals - NON-BLOCKING
 */
(() => {
	const compareCache = new Map();

	const MONEY_FMT = "$" + "{{amount}}";
	const fmt = (c) => {
		if (typeof Shopify?.formatMoney === "function")
			return Shopify.formatMoney(c, window.theme?.moneyFormat || MONEY_FMT);
		return "$" + ((c || 0) / 100).toFixed(2);
	};

	const paint = (cart) => {
		const els = {
			subtotal: document.getElementById("ct-subtotal"),
			total: document.getElementById("ct-total-value"),
			discRow: document.getElementById("ct-discounts-row"),
			disc: document.getElementById("ct-discounts"),
			shipRow: document.getElementById("ct-shipping-row"),
			ship: document.getElementById("ct-shipping"),
			pct: document.getElementById("ct-total-saved-pct"),
		};

		if (!els.total) return;

		const items = cart.items || [];
		const total = cart.total_price || 0;
		const disc = cart.total_discount || 0;

		// Calculate free gift discounts (both tee and snapback)
		let freeGiftDiscount = 0;
		let realSubtotal = 0; // Subtotal excluding free gifts and package protection

		for (const i of items) {
			const title = (i.title || "").toLowerCase();
			const handle = i.product_handle || "";

			// Skip package protection in subtotal
			if (handle === "package-protection") continue;

			// Free gift tee
			if (title.includes("free gift (tee)")) {
				freeGiftDiscount += i.final_line_price || 0;
				continue;
			}
			// Free snapback gift
			if (
				i.properties?.["Free Gift"] === "Snapback" ||
				(title.includes("snapback") && i.price === 0)
			) {
				freeGiftDiscount += i.final_line_price || 0;
				continue;
			}

			// Add to real subtotal (original price before line discounts)
			realSubtotal += i.original_line_price || i.final_line_price || 0;
		}
		window.freeTeeDiscount = freeGiftDiscount;

		// Calculate estimated total (cart total minus free gifts + package protection)
		const baseTotal = total - freeGiftDiscount;
		const ppPrice = window.packageProtectionPrice || 0;
		const estimatedTotal = baseTotal + ppPrice;

		els.total.textContent = fmt(estimatedTotal);

		// Store values for package protection to use
		els.total.dataset.baseTotal = baseTotal;
		els.total.dataset.freeTeeDiscount = freeGiftDiscount;

		// Subtotal shows original prices before any discounts (excluding free gifts)
		if (els.subtotal) els.subtotal.textContent = fmt(realSubtotal);

		if (els.discRow && els.disc) {
			if (disc > 0) {
				els.disc.textContent = "-" + fmt(disc);
				els.discRow.style.display = "flex";
			} else {
				els.discRow.style.display = "none";
			}
		}

		if (els.shipRow && els.ship && items.length) {
			const tb = items.some((i) =>
				(i.title || "").toLowerCase().includes("toolbox"),
			);
			if (total >= 10000 || tb) {
				els.ship.textContent = "FREE";
				els.ship.style.color = "#0F7B0F";
			} else {
				els.ship.textContent = "CALCULATED AT CHECKOUT";
				els.ship.style.color = "#B0B0B1";
			}
			els.shipRow.style.display = "flex";
		}

		if (els.pct) els.pct.style.display = "none";

		// Background: fetch compare-at prices and update savings (non-blocking)
		requestIdleCallback(
			() => {
				const uncached = items.filter(
					(i) => i.handle && !compareCache.has(i.variant_id),
				);
				if (!uncached.length) {
					updateSavings(els, items, disc);
					return;
				}
				Promise.all(
					uncached.map((i) =>
						fetch("/products/" + i.handle + ".js")
							.then((r) => r.json())
							.then((p) => {
								const variant = p.variants?.find(
									(vr) => vr.id === i.variant_id,
								);
								compareCache.set(i.variant_id, variant?.compare_at_price || 0);
							})
							.catch(() => compareCache.set(i.variant_id, 0)),
					),
				).then(() => updateSavings(els, items, disc));
			},
			{ timeout: 2000 },
		);
	};

	const updateSavings = (els, items, baseDisc) => {
		let savings = 0;
		for (const i of items) {
			const cmp = compareCache.get(i.variant_id) || 0;
			if (cmp > i.price) savings += (cmp - i.price) * i.quantity;
		}
		const combined = baseDisc + savings;
		const orig = items.reduce((s, i) => s + i.final_line_price, 0) + combined;

		if (els.subtotal) els.subtotal.textContent = fmt(orig);
		if (els.discRow && els.disc && combined > 0) {
			els.disc.textContent = "-" + fmt(combined);
			els.discRow.style.display = "flex";
		}
		if (els.pct && orig > 0 && combined > 0) {
			els.pct.textContent = Math.round((combined / orig) * 100) + "% SAVED";
			els.pct.style.display = "inline-block";
		}
	};

	window.CartManager?.subscribe(paint);

	// Handle dynamic cart elements
	new MutationObserver((muts) => {
		for (const m of muts) {
			for (const n of m.addedNodes) {
				if (
					n.nodeType === 1 &&
					(n.id?.startsWith("ct-") || n.querySelector?.('[id^="ct-"]'))
				) {
					window.CartManager?.getCart().then(paint);
					return;
				}
			}
		}
	}).observe(document.body, { childList: true, subtree: true });
})();

/**
 * BOGO Rewards
 */
(() => {
	const PRODUCTS = ["tee", "hoodie", "snapback", "sticker", "patch", "long"];
	const TIERS = [
		{ t: 4, f: 1 },
		{ t: 7, f: 2 },
		{ t: 10, f: 3 },
	];

	let containers = null;
	const getC = () => {
		if (!containers)
			containers = [
				...document.querySelectorAll(
					".upcart-rewards-instance, .upcart-rewards",
				),
			];
		return containers;
	};

	const count = (cart) => {
		if (!cart?.items) return 0;
		return cart.items.reduce((c, i) => {
			if (i.price === 0 || i.properties?.["Free Gift"]) return c;
			const t = (i.title || "").toLowerCase();
			const p = (i.product_type || "").toLowerCase();
			if (!PRODUCTS.some((x) => t.includes(x) || p.includes(x))) return c;
			return c + i.quantity * (t.includes("bundle") ? 2 : 1);
		}, 0);
	};

	const update = (cart) => {
		const n = count(cart);
		const cs = getC();
		if (!cs.length) return;

		let earned = 0,
			next = TIERS[0].t;
		for (const tier of TIERS) {
			if (n >= tier.t) earned = tier.f;
			else {
				next = tier.t;
				break;
			}
		}
		const remain = Math.max(0, next - n);

		// Progress
		const pct =
			n >= 10
				? 100
				: n >= 7
					? 72 + ((n - 7) / 3) * 28
					: n >= 4
						? 36 + ((n - 4) / 3) * 36
						: (n / 4) * 36;

		cs.forEach((c) => {
			const bar = c.querySelector(".upcart-rewards-bar-foreground");
			if (bar) {
				bar.style.cssText = `background-color: rgb(0,0,0) !important; width: ${pct}% !important;`;
			}

			const msg = c.querySelector(".upcart-rewards-message p");
			if (msg) {
				if (n === 0)
					msg.textContent =
						"Buy 3, Get 1 FREE  |  Buy 5, Get 2 FREE  |  Buy 7, Get 3 FREE";
				else if (earned && remain)
					msg.textContent = `You've earned ${earned} FREE item${earned > 1 ? "s" : ""}! Add ${remain} more to unlock more.`;
				else if (earned)
					msg.textContent = `You've Earned ${earned} FREE item${earned > 1 ? "s" : ""}.`;
				else
					msg.textContent = `Add ${remain} more item${remain > 1 ? "s" : ""} to unlock 1 FREE item.`;
			}

			c.querySelectorAll(".upcart-rewards-tier").forEach((tier, i) => {
				const icon = tier.querySelector(".upcart-rewards-tier-icon");
				const circle = tier.querySelector(".upcart-rewards-tier-circle");
				if (!icon || !circle) return;

				if (!icon.dataset.init) {
					icon.innerHTML = "";
					icon.style.cssText =
						"display:flex;align-items:center;justify-content:center;font-weight:700;font-size:11px;text-transform:uppercase;";
					icon.textContent = TIERS[i] ? `${TIERS[i].f} FREE` : "🎁";
					icon.dataset.init = "1";
				}

				const unlocked = earned >= i + 1;
				icon.style.color = unlocked ? "white" : "black";
				circle.style.cssText = unlocked
					? "background-color:rgb(0,0,0);fill:rgb(239,239,239);border:none;"
					: "background-color:rgb(239,239,239);fill:rgb(0,0,0);border:2px solid rgb(0,0,0);";
			});
		});
	};

	window.CartManager?.subscribe(update);

	new MutationObserver(() => {
		const newC = [
			...document.querySelectorAll(".upcart-rewards-instance, .upcart-rewards"),
		];
		if (newC.length !== (containers?.length || 0)) {
			containers = newC;
			window.CartManager?.getCart().then(update);
		}
	}).observe(document.body, { childList: true, subtree: true });
})();

/**
 * Country Banner
 */
(() => {
	let tries = 0;
	const check = () => {
		const r = document.getElementById("reward-banner");
		const s = document.getElementById("shipping-banner");
		if (!r || !s) {
			tries += 1;
			if (tries < 20) setTimeout(check, 300);
			return;
		}
		if (window.Shopify?.country && window.Shopify.country !== "US") {
			r.style.display = "none";
			s.style.display = "none";
		}
	};
	check();
})();

/**
 * UI Visibility for Free Gift Only Cart
 */
(() => {
	const updateUI = (cart) => {
		if (!cart?.items) return;

		let hasFreeGift = false;
		let hasOtherItems = false;

		for (const item of cart.items) {
			const title = (item.title || "").toLowerCase();
			if (title.includes("free gift (tee)")) {
				hasFreeGift = true;
			} else {
				hasOtherItems = true;
			}
		}

		const onlyFreeGift = hasFreeGift && !hasOtherItems;
		window.onlyFreeGiftInCart = onlyFreeGift;

		const cartTotals = document.getElementById("cart-totals");
		const cartTotalsBorder = document.getElementById("cart-totals-border");
		const cartForm = document.getElementById("cart-form");

		if (onlyFreeGift) {
			if (cartTotals) cartTotals.style.display = "none";
			if (cartTotalsBorder) cartTotalsBorder.style.display = "none";
			if (cartForm) cartForm.style.display = "none";
		} else {
			if (cartTotals) cartTotals.style.display = "flex";
			if (cartTotalsBorder) cartTotalsBorder.style.display = "block";
			if (cartForm) cartForm.style.display = "flex";
		}
	};

	window.CartManager?.subscribe(updateUI);
})();
